<template>
  <div
    v-if="isOpen"
    class="overlay bg-gray-600 bg-opacity-50 flex items-center justify-center z-50"
    @click.self="closeModal"
  >
    <div
      class="bg-white rounded-lg shadow-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col"
      @click.stop
    >
      <div class="flex flex-row items-center justify-between mb-4">
        <h3 class="text-[#061B44] font-semibold text-lg">
          {{ $t("pdf-preview") || "PDF Preview" }}
        </h3>
        <button
          @click="closeModal"
          class="text-subDarkBlue hover:text-subDark opacity-70 hover:opacity-100 text-xl font-bold"
        >
          ×
        </button>
      </div>

      <!-- Formatting Toolbar -->
      <div class="toolbar mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
        <div class="flex flex-row items-center gap-2 flex-wrap">
          <!-- Alignment Buttons -->
          <div class="flex flex-row items-center gap-1 border-r border-gray-300 pr-2 mr-2">
            <button
              @click="setAlignmentForSelected('left')"
              :class="{ 'bg-blue-500 text-white': selectedAlignment === 'left' }"
              class="px-3 py-1.5 rounded text-sm font-medium hover:bg-gray-200 transition-colors"
              :title="$t('align-left') || 'Align Left'"
            >
              <i class="pi pi-align-left"></i>
            </button>
            <button
              @click="setAlignmentForSelected('center')"
              :class="{ 'bg-blue-500 text-white': selectedAlignment === 'center' }"
              class="px-3 py-1.5 rounded text-sm font-medium hover:bg-gray-200 transition-colors"
              :title="$t('align-center') || 'Align Center'"
            >
              <i class="pi pi-align-center"></i>
            </button>
            <button
              @click="setAlignmentForSelected('right')"
              :class="{ 'bg-blue-500 text-white': selectedAlignment === 'right' }"
              class="px-3 py-1.5 rounded text-sm font-medium hover:bg-gray-200 transition-colors"
              :title="$t('align-right') || 'Align Right'"
            >
              <i class="pi pi-align-right"></i>
            </button>
            <button
              @click="setAlignmentForSelected('justify')"
              :class="{ 'bg-blue-500 text-white': selectedAlignment === 'justify' }"
              class="px-3 py-1.5 rounded text-sm font-medium hover:bg-gray-200 transition-colors"
              :title="$t('align-justify') || 'Justify'"
            >
              <i class="pi pi-align-justify"></i>
            </button>
          </div>

          <!-- Formatting Buttons -->
          <div class="flex flex-row items-center gap-1">
            <button
              @click="toggleFormatForSelected('bold')"
              :class="{ 'bg-blue-500 text-white': selectedFormats.bold }"
              class="px-3 py-1.5 rounded text-sm font-bold hover:bg-gray-200 transition-colors"
              :title="$t('bold') || 'Bold'"
            >
              <i class="pi pi-bold"></i>
            </button>
            <button
              @click="toggleFormatForSelected('italic')"
              :class="{ 'bg-blue-500 text-white': selectedFormats.italic }"
              class="px-3 py-1.5 rounded text-sm italic hover:bg-gray-200 transition-colors"
              :title="$t('italic') || 'Italic'"
            >
              <i class="pi pi-italic"></i>
            </button>
            <button
              @click="toggleFormatForSelected('underline')"
              :class="{ 'bg-blue-500 text-white': selectedFormats.underline }"
              class="px-3 py-1.5 rounded text-sm hover:bg-gray-200 transition-colors underline"
              :title="$t('underline') || 'Underline'"
            >
              <i class="pi pi-underline"></i>
            </button>
          </div>
        </div>
      </div>

      <div class="flex-1 overflow-y-auto mb-4">
        <div
          ref="previewContainerRef"
          class="pdf-preview-container"
          :style="previewStyles"
        >
          <div
            v-for="(paragraph, index) in paragraphs"
            :key="index"
            :contenteditable="true"
            @focus="selectParagraph(index)"
            @blur="updateParagraphText(index, $event)"
            @input="updateParagraphText(index, $event)"
            class="preview-paragraph editable-paragraph"
            :class="{ 'selected': selectedParagraphIndex === index }"
            :style="getParagraphStyle(index)"
            v-text="paragraph.text"
          ></div>
        </div>
      </div>

      <div
        class="flex flex-row gap-3 justify-end pt-4 border-t"
        :class="{ 'flex-row-reverse': isRtl }"
      >
        <button
          @click="closeModal"
          class="px-6 py-2 bg-white text-slate-900 border-slate-400 border-[1px] rounded-md font-normal text-base hover:bg-gray-50"
        >
          {{ $t("cancel") || "Cancel" }}
        </button>
        <button
          @click="handleExport"
          class="px-6 py-2 text-white bg-slate-900 rounded-md font-normal text-base hover:bg-slate-800 flex items-center space-x-2"
          :class="{ 'space-x-reverse': isRtl }"
        >
          <i class="pi pi-download"></i>
          <span>{{ $t("export-pdf") || "Export PDF" }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";

interface ParagraphFormat {
  text: string;
  alignment: 'left' | 'center' | 'right' | 'justify';
  bold: boolean;
  italic: boolean;
  underline: boolean;
}

interface Props {
  isOpen: boolean;
  text: string;
  fontFamily: string;
  fontSize: number;
  color: string;
  isRtl: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  close: [];
  export: [editedText: string, formatting: ParagraphFormat[]];
}>();

const previewContainerRef = ref<HTMLElement | null>(null);
const selectedParagraphIndex = ref<number | null>(null);
const selectedAlignment = ref<'left' | 'center' | 'right' | 'justify'>('left');
const selectedFormats = ref({ bold: false, italic: false, underline: false });

const editableParagraphs = ref<ParagraphFormat[]>([]);

watch(() => props.text, (newText) => {
  if (newText) {
    const paragraphs = newText
      .split(/\n\n+/)
      .filter((p: string) => p.trim().length > 0)
      .map((p: string) => p.trim());
    
    editableParagraphs.value = paragraphs.map((p: string) => ({
      text: p,
      alignment: props.isRtl ? 'right' : 'left',
      bold: false,
      italic: false,
      underline: false,
    }));
  }
}, { immediate: true });

watch(() => props.isOpen, (isOpen) => {
  if (isOpen && props.text) {
    const paragraphs = props.text
      .split(/\n\n+/)
      .filter((p: string) => p.trim().length > 0)
      .map((p: string) => p.trim());
    
    editableParagraphs.value = paragraphs.map((p: string) => ({
      text: p,
      alignment: props.isRtl ? 'right' : 'left',
      bold: false,
      italic: false,
      underline: false,
    }));
    selectedParagraphIndex.value = null;
    selectedAlignment.value = props.isRtl ? 'right' : 'left';
    selectedFormats.value = { bold: false, italic: false, underline: false };
  }
});

const paragraphs = computed(() => {
  return editableParagraphs.value;
});

const previewStyles = computed(() => {
  const pageWidthMM = 210;
  const pageHeightMM = 297;
  const marginMM = 20;
  const contentWidthMM = pageWidthMM - marginMM * 2;
  const dpi = 96;
  const contentWidthPX = Math.round((contentWidthMM / 25.4) * dpi);
  const pageHeightPX = Math.round((pageHeightMM / 25.4) * dpi);
  const marginPX = Math.round((marginMM / 25.4) * dpi);
  // Match audio PDF export: page container width = content + left/right margins (not content-only width with padding shrinking text)
  const fullPageWidthPX = contentWidthPX + marginPX * 2;

  return {
    width: `${fullPageWidthPX}px`,
    minWidth: `${fullPageWidthPX}px`,
    maxWidth: `${fullPageWidthPX}px`,
    minHeight: `${pageHeightPX}px`,
    padding: `${marginPX}px`,
    backgroundColor: "#ffffff",
    fontFamily: props.fontFamily || "Arial",
    fontSize: `${props.fontSize}pt`,
    color: props.color || "#000000",
    direction: (props.isRtl ? "rtl" : "ltr") as "rtl" | "ltr",
    textAlign: (props.isRtl ? "right" : "left") as "right" | "left",
    lineHeight: "1.2",
    wordWrap: "break-word" as const,
    overflowWrap: "break-word" as const,
    boxSizing: "border-box" as const,
    margin: "0 auto",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  } as const;
});

const getParagraphStyle = (index: number) => {
  const para = editableParagraphs.value[index];
  if (!para) return {};
  
  return {
    margin: "0",
    marginTop: index > 0 ? props.fontSize * 0.8 + "pt" : "0",
    marginBottom: "0",
    padding: "0",
    lineHeight: "1.2",
    wordWrap: "break-word" as const,
    overflowWrap: "break-word" as const,
    whiteSpace: "normal" as const,
    display: "block" as const,
    width: "100%",
    boxSizing: "border-box" as const,
    textAlign: para.alignment as "left" | "right" | "center" | "justify",
    fontWeight: (para.bold ? "bold" : "normal") as "bold" | "normal",
    fontStyle: (para.italic ? "italic" : "normal") as "italic" | "normal",
    textDecoration: (para.underline ? "underline" : "none") as "underline" | "none",
    minHeight: "20px",
    outline: "none",
  } as const;
};

const selectParagraph = (index: number) => {
  selectedParagraphIndex.value = index;
  const para = editableParagraphs.value[index];
  if (para) {
    selectedAlignment.value = para.alignment;
    selectedFormats.value = {
      bold: para.bold,
      italic: para.italic,
      underline: para.underline,
    };
  }
};

const updateParagraphText = (index: number, event: Event) => {
  const target = event.target as HTMLElement;
  if (editableParagraphs.value[index]) {
    const newText = target.textContent || target.innerText || "";
    editableParagraphs.value[index].text = newText;
  }
};

const setAlignmentForSelected = (alignment: 'left' | 'center' | 'right' | 'justify') => {
  selectedAlignment.value = alignment;
  if (selectedParagraphIndex.value !== null) {
    editableParagraphs.value[selectedParagraphIndex.value].alignment = alignment;
  }
};

const toggleFormatForSelected = (format: 'bold' | 'italic' | 'underline') => {
  if (selectedParagraphIndex.value !== null) {
    const para = editableParagraphs.value[selectedParagraphIndex.value];
    if (format === 'bold') {
      para.bold = !para.bold;
      selectedFormats.value.bold = para.bold;
    } else if (format === 'italic') {
      para.italic = !para.italic;
      selectedFormats.value.italic = para.italic;
    } else if (format === 'underline') {
      para.underline = !para.underline;
      selectedFormats.value.underline = para.underline;
    }
  }
};

const closeModal = () => {
  emit("close");
};

const handleExport = () => {
  const editedText = editableParagraphs.value.map(p => p.text).join("\n\n");
  emit("export", editedText, editableParagraphs.value);
};
</script>

<style scoped>
.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.pdf-preview-container {
  border: 1px solid #e5e7eb;
}

.preview-paragraph:first-child {
  margin-top: 0 !important;
}

.editable-paragraph {
  cursor: text;
  border: 1px solid transparent;
  transition: border-color 0.2s;
}

.editable-paragraph:hover {
  border-color: #e5e7eb;
}

.editable-paragraph.selected {
  border-color: #3b82f6;
  background-color: #eff6ff;
}

.editable-paragraph:focus {
  outline: none;
  border-color: #3b82f6;
  background-color: #eff6ff;
}
</style>

